/**
 * WebSocket Hook
 * Manages team-level WebSocket connection with message handling
 *
 * This is a low-level hook that manages the WebSocket connection.
 * Use WebSocketContext for application-level WebSocket functionality.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import type { BaseWebSocketMessage } from '@zenmako/contracts/websocket';

// WebSocket message type - use contracts package
export type WebSocketMessage = BaseWebSocketMessage & {
  payload: any;
};

export interface UseWebSocketOptions {
  userId: string;
  teamId: string;
  token: string;
  onMessage?: (message: WebSocketMessage) => void;
  autoConnect?: boolean;
}

export interface UseWebSocketReturn {
  connected: boolean;
  send: (message: WebSocketMessage) => void;
  connect: () => void;
  disconnect: () => void;
  lastMessage: WebSocketMessage | null;
}

const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:4001/ws';
const RECONNECT_INTERVAL = 3000;
const MAX_RECONNECT_ATTEMPTS = 5;

/**
 * Map backend message types to frontend message types
 */
function mapMessageType(backendType: string): string {
  const typeMap: Record<string, string> = {
    'chat/message@v1': 'thread.message',
    'chat/response@v1': 'thread.message',
    'tool/result@v1': 'thread.message',
    // Execution status types from websocket event-handler (tool/result@v1 maps to these)
    'execution.completed': 'execution.completed',
    'execution.approval_required': 'execution.approval_required',
    'execution.error': 'execution.error',
    'user.message': 'user.message',
    'agent.response': 'agent.response',
    'executor.invoked': 'executor.invoked',
    'user.joined': 'user.joined',
    'knowledge_base.updated': 'knowledge_base.updated',
  };

  return typeMap[backendType] || backendType;
}

/**
 * Low-level WebSocket connection hook
 *
 * Features:
 * - Auto-reconnection with exponential backoff
 * - Connection state management
 * - Message handling with callback
 *
 * @param options - WebSocket configuration options
 * @returns WebSocket connection state and methods
 */
export function useWebSocket(options: UseWebSocketOptions): UseWebSocketReturn {
  const { userId, teamId, token, onMessage, autoConnect = true } = options;

  const ws = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimeout = useRef<number | null>(null);

  const [connected, setConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);

  const connect = () => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      console.log('[WebSocket] Already connected');
      return;
    }

    const url = `${WS_URL}?userId=${encodeURIComponent(userId)}&teamId=${encodeURIComponent(teamId)}&token=${encodeURIComponent(token)}`;

    console.log('[WebSocket] Connecting to:', WS_URL);

    try {
      ws.current = new WebSocket(url);

      ws.current.onopen = () => {
        console.log('[WebSocket] Connected');
        setConnected(true);
        reconnectAttempts.current = 0;
      };

      ws.current.onmessage = (event) => {
        try {
          const rawMessage = JSON.parse(event.data);
          console.log('[WebSocket] Raw message received:', rawMessage);
          console.log('[WebSocket] Message ID extraction debug:', {
            hasData: !!rawMessage.data,
            dataMessageId: rawMessage.data?.messageId,
            rawMessageId: rawMessage.messageId,
            finalMessageId: rawMessage.data?.messageId || rawMessage.messageId || `msg-${Date.now()}`
          });

          // Map backend message format to frontend format
          const message: WebSocketMessage = {
            messageType: mapMessageType(rawMessage.type),
            // Use messageId from event detail (timestamp format), not EventBridge eventId (UUID)
            messageId: rawMessage.data?.messageId || rawMessage.messageId || `msg-${Date.now()}`,
            timestamp: rawMessage.timestamp ? new Date(rawMessage.timestamp).getTime() : Date.now(),
            payload: rawMessage.data || rawMessage.payload || {},
          };

          console.log('[WebSocket] Mapped message:', message.messageType, message);

          setLastMessage(message);

          if (onMessage) {
            onMessage(message);
          }
        } catch (error) {
          console.error('[WebSocket] Failed to parse message:', error);
        }
      };

      ws.current.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
      };

      ws.current.onclose = () => {
        console.log('[WebSocket] Disconnected');
        setConnected(false);
        ws.current = null;

        // Attempt reconnection
        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttempts.current++;
          console.log(
            `[WebSocket] Reconnecting in ${RECONNECT_INTERVAL}ms (attempt ${reconnectAttempts.current}/${MAX_RECONNECT_ATTEMPTS})`
          );
          reconnectTimeout.current = window.setTimeout(() => {
            connect();
          }, RECONNECT_INTERVAL);
        } else {
          console.error('[WebSocket] Max reconnection attempts reached');
        }
      };
    } catch (error) {
      console.error('[WebSocket] Connection error:', error);
    }
  };

  const disconnect = () => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }

    if (ws.current) {
      console.log('[WebSocket] Disconnecting');
      ws.current.close();
      ws.current = null;
    }

    setConnected(false);
  };

  const send = (message: WebSocketMessage) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
      console.log('[WebSocket] Message sent:', message.messageType);
    } else {
      console.warn('[WebSocket] Cannot send message, not connected');
    }
  };

  useEffect(() => {
    if (autoConnect && userId && teamId && token) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [userId, teamId, token, autoConnect]);

  return {
    connected,
    send,
    connect,
    disconnect,
    lastMessage,
  };
}
